import axios from 'axios';
import { debounce } from 'lodash/fp';

import EventsClient from 'lib/clients/EventsClient';
import {
  BaseMediaRecorderClient,
  MediaRecorderClientBaseParams,
  VIDEO_BITS_PER_SECOND,
} from 'lib/clients/mediaRecorder/BaseMediaRecorderClient';
import { MediaRecorderManager } from 'lib/clients/mediaRecorder/MediaRecorderManager';
import { jwtDecode } from 'lib/jwt';
import { captureException, captureMessage } from 'lib/reporting';

export interface WebrtcVideoData {
  type: 'stream';
  objectId: string;
}

export interface WebrtcMediaRecorderClientParams extends MediaRecorderClientBaseParams {
  jwt: string | undefined | null;
  errorOnSlowComplete?: boolean;
  enableRestartIce?: boolean;
}

type ServerConfig = {
  username: string;
  credential: string;
  server_url: string;
};

enum MessageType {
  UploadedAssets = 'uploaded_assets',
  StopRecording = 'stop_recording',
  StoppedRecording = 'stopped_recording',
  TimedOut = 'timed_out',
  ClientConnected = 'client_connected',
  ServerConnected = 'server_connected',
}

interface UploadedAssets {
  message_type: MessageType.UploadedAssets;
  object_id: string;
}

interface StopRecording {
  message_type: MessageType.StopRecording;
}

interface StoppedRecording {
  message_type: MessageType.StoppedRecording;
  stats: {
    packets_received: number;
    packets_lost: number;
  };
}

interface TimedOut {
  message_type: MessageType.TimedOut;
}

interface ClientConnected {
  message_type: MessageType.ClientConnected;
}

interface ServerConnected {
  message_type: MessageType.ServerConnected;
}

type Message =
  | UploadedAssets
  | StopRecording
  | StoppedRecording
  | TimedOut
  | ClientConnected
  | ServerConnected;

const WEBRTC_CONSUMER_BASE_URL = import.meta.env.VITE_WEBRTC_CONSUMER_URL;

export const ICE_CANDIDATE_GATHERING_ERROR = "Couldn't gather enough ICE Candidates";

const MAX_ICE_CANDIDATE_GATHERING_MS = 1000;

const MIN_NUM_ICE_CANDIDATES = 2;

/**
 * Interface for handling an instance of a MediaRecorder. This Client is a singleton that uses an Observer model so it can
 * live across multiple routes / views
 */
class Client extends BaseMediaRecorderClient<WebrtcMediaRecorderClientParams> {
  private peerConnection?: RTCPeerConnection;
  private dataChannel?: RTCDataChannel;
  private hasStarted = false;
  private hasFinishedIce = false;
  private hasRestartedIce = false;
  private hasReceivedUpload = false;

  public type = 'stream' as const;

  public init(params: WebrtcMediaRecorderClientParams): string | undefined {
    this.params = params;
    const { stream, onStart, onError, onRecordingComplete, onTimeExceeded } = params;

    if (!this.isSupported()) {
      onError?.('MediaRecorder error: MediaRecorder is not supported');
      return;
    }

    if (!stream) {
      onError?.('MediaRecorder error: stream must be provided');
      return;
    }

    if (!params.jwt) {
      onError?.('MediaRecorder error: jwt must be provided');
      return;
    }

    return this.subscribe({
      onStart,
      onError,
      onRecordingComplete,
      onTimeExceeded,
    });
  }

  private isConnected() {
    // Firefox doesn't implement `connectionState`, so fall back to `iceConnectionState` if we have to
    return (
      this.peerConnection?.connectionState === 'connected' ||
      this.peerConnection?.iceConnectionState === 'connected'
    );
  }

  public get state() {
    return this.peerConnection?.connectionState || this.peerConnection?.iceConnectionState;
  }

  public isSupported() {
    return !!window.RTCPeerConnection;
  }

  public isRecording() {
    // TODO(bryan): fix this
    return this.isConnected();
  }

  public startRecording() {
    // Return pre-maturely in case if this function is called multiple times,
    // see sentry error PERSONA-WEB-INQUIRY-3A8
    if (this.hasStarted) {
      return;
    }

    this.hasStarted = true;

    if (!this.params?.jwt) {
      this.handleError(new Error('WebrtcMediaRecorderClient: missing jwt'));
      return;
    }

    this.getServerConfig(this.params.jwt)
      .then((serverConfig) => {
        return this.createPeerConnection(serverConfig, this.params?.jwt as string);
      })
      .catch((e) => this.handleError(e));

    return this;
  }

  public stopCapture() {
    EventsClient.logAmplitude('webrtc.stop-recording');

    if (this.isConnected() && this.dataChannel) {
      this.sendMessage({ message_type: MessageType.StopRecording });

      if (this.params?.errorOnSlowComplete) {
        setTimeout(() => {
          if (!this.hasReceivedUpload) {
            this.handleError(
              new Error('WebrtcMediaRecorderClient: Recording took too long to complete'),
            );
          }
        }, 20000); // Throw an error if we don't receive the upload in 20s
      }
    }

    return this;
  }

  public destroy(): void {
    this.closePeerConnection();
    this.clearTimeouts();
    this.clearSubscribers();
    this.hasStarted = false;
    this.hasFinishedIce = false;
    this.hasReceivedUpload = false;
  }

  private getBaseUrl(jwt: string): string {
    const parsedJwt = jwtDecode(jwt);
    if (!parsedJwt || parsedJwt?.aud === 'persona') {
      return WEBRTC_CONSUMER_BASE_URL;
    }
    return parsedJwt.aud as string;
  }

  private getServerConfig(jwt: string): Promise<ServerConfig> {
    return axios
      .get(`${this.getBaseUrl(jwt)}/server-config?jwt=${jwt}`)
      .then((response) => response.data);
  }

  private createPeerConnection(serverConfig: ServerConfig, jwt: string): Promise<void> {
    const config: RTCConfiguration = {
      iceTransportPolicy: 'relay',
      iceServers: [
        {
          urls: [serverConfig.server_url],
          username: serverConfig.username,
          credential: serverConfig.credential,
        },
      ],
    };

    if (!this.params?.stream) {
      this.handleError(new Error('WebrtcMediaRecorderClient: missing stream'));
      return Promise.resolve();
    }

    this.peerConnection = new RTCPeerConnection(config);
    // Workaround: We need to create a datachannel from the client in order for the server's
    // datachannel to ever connect. This data channel is unused and basically thrown away.
    this.peerConnection.createDataChannel('clientChannel', { ordered: true });

    this.params?.stream.getTracks().forEach((track) => {
      this.peerConnection?.addTrack(track, this.params?.stream as MediaStream);
      if (track.kind === 'video') {
        // Set the codec to VP8 or VP9 if possible (avoids weird video transcoding issues on the server)
        const capabilities = RTCRtpSender.getCapabilities?.('video');
        if (capabilities && capabilities.codecs) {
          const vpxCodecs = capabilities.codecs.filter((c) => c.mimeType.includes('video/VP'));
          if (vpxCodecs.length) {
            if (this.peerConnection?.getTransceivers) {
              const transceiver = this.peerConnection
                .getTransceivers()
                .find((t) => t.sender && t.sender.track === track);
              if (transceiver && transceiver.setCodecPreferences) {
                transceiver.setCodecPreferences(vpxCodecs);
              }
            }
          }
        }
      }
    });

    this.peerConnection.onconnectionstatechange = () => {
      switch (this.peerConnection?.connectionState) {
        case 'new':
        case 'disconnected':
        case 'closed':
          break;
        case 'connected':
          this.setEncodings();
          this.logStats(this.peerConnection, 'connected');
          break;
        case 'failed':
          this.logStats(this.peerConnection, 'failed');

          if (this.params?.enableRestartIce && !this.hasFinishedIce) {
            // don't bubble up an error in this case as we are going to reattempt ice
          } else {
            this.handleError(new Error('Connection failed'));
          }

          break;
        default:
          break;
      }
    };

    this.peerConnection.ondatachannel = (ev) => {
      this.dataChannel = ev.channel;
      this.dataChannel.onmessage = (messageEvent) => {
        try {
          const parsedMessage = JSON.parse(messageEvent.data);
          this.handleMessage(parsedMessage.message);
        } catch (e) {
          this.handleError(e);
        }
      };
      this.dataChannel.addEventListener('open', () => {
        this.sendMessage({ message_type: MessageType.ClientConnected });
      });
    };

    if (this.params?.enableRestartIce) {
      this.peerConnection.oniceconnectionstatechange = () => {
        if (this.peerConnection?.iceConnectionState === 'failed' && !this.hasFinishedIce) {
          if (!this.hasRestartedIce) {
            EventsClient.logAmplitude('webrtc.ice-restart');
            this.hasRestartedIce = true;
            this.peerConnection.restartIce();
            this.createOfferAndSetDescriptions(this.peerConnection, jwt, true);
          } else {
            this.handleError('ICE connection failed');
          }
        }
      };
    }
    return this.createOfferAndSetDescriptions(this.peerConnection, jwt, false);
  }

  private createOfferAndSetDescriptions = (
    peerConnection: RTCPeerConnection,
    jwt: string,
    iceRestart: boolean,
  ): Promise<void> => {
    return peerConnection
      .createOffer({
        offerToReceiveVideo: false,
        offerToReceiveAudio: false,
        iceRestart,
      })
      .then((offer) => {
        return this.peerConnection?.setLocalDescription(offer);
      })
      .then(() => {
        // wait for ICE gathering to complete
        return new Promise<void>((resolve, reject) => {
          if (this.peerConnection?.iceGatheringState === 'complete') {
            resolve();
          } else {
            let iceCandidateCount = 0;

            // Set a debounced function that only completes after 250ms of no longer receiving candidates
            // If we meet the minimum number of candidates by then, attempt to start the stream
            // (usually candidates that take a long time to recieve are not viable anyway, i.e. will result in icecandidateerror)
            const debouncedCompleteGathering = debounce(250, () => {
              if (iceCandidateCount >= MIN_NUM_ICE_CANDIDATES) {
                cleanUpListeners();
                resolve();
              }
            });

            // If after MAX_ICE_CANDIDATE_GATHERING_MS we still have not resolved, either force the stream to start or
            // throw an error so that we can fallback to upload
            const timeout = setTimeout(() => {
              if (iceCandidateCount >= MIN_NUM_ICE_CANDIDATES) {
                cleanUpListeners();
                resolve();
              } else {
                cleanUpListeners();
                reject(new Error(ICE_CANDIDATE_GATHERING_ERROR));
              }
            }, MAX_ICE_CANDIDATE_GATHERING_MS);

            const checkState = () => {
              if (this.peerConnection?.iceGatheringState === 'complete') {
                cleanUpListeners();
                resolve();
              }
            };
            const onCandidateError = (event: RTCPeerConnectionIceErrorEvent) => {
              captureMessage('WebrtcMediaRecorderClient: ICECandidateError', 'info', {
                errorCode: event?.errorCode,
                errorText: event?.errorText,
                url: event?.url,
                host: event?.hostCandidate,
              });
            };

            const countCandidate = () => {
              iceCandidateCount++;
              debouncedCompleteGathering();
            };

            const cleanUpListeners = () => {
              debouncedCompleteGathering.cancel();
              clearTimeout(timeout);
              this.peerConnection?.removeEventListener('icegatheringstatechange', checkState);
              this.peerConnection?.removeEventListener('icecandidateerror', onCandidateError);
              this.peerConnection?.removeEventListener('icecandidate', countCandidate);
            };
            this.peerConnection?.addEventListener('icegatheringstatechange', checkState);
            this.peerConnection?.addEventListener('icecandidateerror', onCandidateError);
            this.peerConnection?.addEventListener('icecandidate', countCandidate);
          }
        });
      })
      .then(() => {
        const offer = this.peerConnection?.localDescription;
        const videoSettings = this.params?.stream.getVideoTracks()[0].getSettings();
        return axios.post(`${this.getBaseUrl(jwt)}/connect`, {
          sdp: offer?.sdp,
          type: offer?.type,
          width: videoSettings?.width,
          height: videoSettings?.height,
          jwt: this.params?.jwt,
        });
      })
      .then(({ data: { answer } }) => {
        this.hasFinishedIce = true;
        return this.peerConnection?.setRemoteDescription(answer);
      });
  };

  private setEncodings = () => {
    this.peerConnection?.getSenders?.().forEach((sender) => {
      if (sender.track?.kind === 'video') {
        const videoSettings = sender.track.getSettings();
        const parameters = sender.getParameters();
        // Note: TS ignores here are because the webrtc types are incomplete. Browser compatibility is fine here.
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        parameters.encodings.forEach((encoding) => {
          encoding.maxBitrate = this.params?.bitRate ?? VIDEO_BITS_PER_SECOND;
          if (
            videoSettings.width &&
            videoSettings.height &&
            (videoSettings.width > 1024 || videoSettings.height > 720)
          ) {
            encoding.scaleResolutionDownBy = 2.0;
          }
        });
        sender.setParameters(parameters);
      }
    });
  };

  private handleMessage = (message: Message) => {
    switch (message.message_type) {
      case MessageType.ServerConnected:
        this.onStartedRecording();
        break;
      case MessageType.UploadedAssets: {
        const objectId = message.object_id;
        this.hasReceivedUpload = true;
        this.handleComplete(objectId);
        EventsClient.logAmplitude('webrtc.uploaded-assets');
        this.closePeerConnection();
        break;
      }
      case MessageType.TimedOut:
        this.handleTimeExceeded();
        break;
      default:
        break;
    }
  };

  private sendMessage = (message: Message) => {
    try {
      this.dataChannel?.send(JSON.stringify({ message }));
    } catch (e) {
      captureException(e, 'warning', { dataChannelReadyState: this.dataChannel?.readyState });

      // Only throw if the data channel is open and we encountered an error; otherwise we should swallow the errors
      // and log the exception silently
      if (this.dataChannel?.readyState === 'open') {
        this.handleError(e);
      }
    }
  };

  private closePeerConnection = () => {
    if (this.peerConnection?.connectionState === 'closed') {
      return;
    }
    this.dataChannel?.close();

    if (this.peerConnection?.getTransceivers) {
      this.peerConnection.getTransceivers().forEach((transceiver) => {
        try {
          transceiver.stop();
        } catch {
          // If we can't stop, probably means the connection is already stopped
        }
      });
    }

    try {
      this.peerConnection?.close();
    } catch {
      // If we can't stop, probably means the connection is already closed
    }
  };

  private handleComplete = (objectId: string) => {
    this.notify('stop', { objectId, type: this.type });
  };

  private handleError = (e: Error | unknown) => {
    if (MediaRecorderManager.currentClient?.type === this.type) {
      this.notify('error', e);
    }
  };

  private handleTimeExceeded = () => {
    this.notify('time-exceeded');
  };

  private logStats = async (peerConnection: RTCPeerConnection, connectionState: string) => {
    peerConnection
      .getStats?.()
      .then((stats) => {
        const kinds: string[] = [];
        const mimeTypes: string[] = [];
        const bytesSent: string[] = [];
        const bytesReceived: string[] = [];
        const candidatePairState: string[] = [];
        const roundTripTimes: string[] = [];
        const outboundRtp: string[] = [];
        const remoteInboundRtp: string[] = [];
        const remoteOutboundRtp: string[] = [];
        let transport: string[] = [];

        try {
          stats.forEach((report) => {
            switch (report.type) {
              case 'media-source': {
                kinds.push(`${report.id}-${report.kind}`);
                break;
              }
              case 'transport': {
                transport = [
                  report.dtlsState,
                  report.iceState,
                  report.iceRole,
                  report.packetsSent,
                  report.packetsRecieved,
                ];
                break;
              }
              case 'candidate-pair': {
                const id = report.id;
                bytesSent.push(`${id}-${report.bytesSent}`);
                bytesReceived.push(`${id}-${report.bytesReceived}`);
                candidatePairState.push(`${id}-${report.state}`);
                roundTripTimes.push(`${id}-cp-${report.currentRoundTripTime}`);
                break;
              }
              case 'outbound-rtp': {
                outboundRtp.push(
                  [
                    report.id,
                    report.framesEncoded,
                    report.nackCount,
                    report.firCount,
                    report.averageRtcpInterval,
                    report.totalPacketSendDelay,
                  ].join('-'),
                );
                break;
              }
              case 'remote-inbound-rtp': {
                remoteInboundRtp.push(
                  [
                    report.localId,
                    report.roundTripTime,
                    report.totalRoundTripTime,
                    report.fractionLost,
                  ].join('-'),
                );
                break;
              }
              case 'remote-outbound-rtp': {
                remoteOutboundRtp.push(
                  [report.localId, report.roundTripTime, report.totalRoundTripTime].join('-'),
                );
                break;
              }
              case 'codec': {
                mimeTypes.push(report.mimeType);
                break;
              }
              default:
              // do nothing
            }
          });
        } catch (e) {
          // if something goes wrong, just ignore
        }

        EventsClient.logAmplitude('webrtc.stats', {
          state: connectionState,
          kinds: kinds.join(','),
          transport: transport.join(','),
          bytesSent: bytesSent.join(','),
          bytesReceived: bytesReceived.join(','),
          candidatePairState: candidatePairState.join(','),
          roundTripTimes: roundTripTimes.join(','),
          mimeTypes: mimeTypes.join(','),
          outboundRtp: outboundRtp.join(','),
          remoteInboundRtp: remoteInboundRtp.join(','),
          remoteOutboundRtp: remoteOutboundRtp.join(','),
        });
      })
      .catch((_error) => {
        // Do nothing as we just need this for logging
      });
  };
}

export const WebrtcMediaRecorderClient = new Client();
